// src/components/widgets/CumpleanosWidget.js
// Widget "Cumpleaños del equipo" — visible para TODOS los usuarios (sin
// permiso, igual que el Manual de Uso). Lee 1 doc precalculado
// (`widgets_datos/cumpleanos`, lo arma cronCumpleanosWidget a las 00:15 GT
// con SOLO nombre + email interno + día/mes de colaboradores ACTIVOS — nunca
// la fecha de nacimiento completa ni datos del expediente).
// Si quien lo ve es el cumpleañero, la card se convierte en su felicitación.
// Si no hay cumpleaños hoy ni en los próximos 7 días, no se muestra nada.
import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { CakeIcon, SparklesIcon } from '../ui/Icons';
import useEsMovil from '../../hooks/useEsMovil';
import { ESTILO_TILE } from './widgetsUI';

// Animación propia (keyframes con prefijo del componente, regla de la casa)
const estilosCumple = `
  @keyframes cumple-pop {
    0% { transform: scale(0.6); opacity: 0; }
    70% { transform: scale(1.12); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes cumple-balanceo {
    0%, 100% { transform: rotate(-4deg); }
    50% { transform: rotate(4deg); }
  }
  .cumple-icono-festivo {
    animation: cumple-pop 0.5s ease-out, cumple-balanceo 2.4s ease-in-out 0.5s 3;
  }
  @media (prefers-reduced-motion: reduce) {
    .cumple-icono-festivo { animation: none; }
  }
  @media (max-width: 768px) {
    .cumple-widget-container { margin: 15px 0 !important; }
    .cumple-card { padding: 12px !important; }
  }
`;

const hoyGT = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Guatemala' }).format(new Date());

function CumpleanosWidget() {
  const [datos, setDatos] = useState(null);
  const { user } = useAuth();
  const isMobile = useEsMovil();

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'widgets_datos', 'cumpleanos'));
        if (vivo && snap.exists()) setDatos(snap.data());
      } catch (e) {
        console.error('Error cargando cumpleaños del equipo:', e);
      }
    })();
    return () => { vivo = false; };
  }, []);

  // Sin doc, doc de otro día (cron aún no corrió) o sin cumpleaños a la
  // vista → el widget no ocupa espacio (tampoco muestra skeleton: casi
  // siempre su estado natural es "nada que anunciar").
  if (!datos || datos.fecha !== hoyGT()) return null;
  const cumpleHoy = datos.hoy || [];
  const proximos = datos.proximos || [];
  if (cumpleHoy.length === 0 && proximos.length === 0) return null;

  const emailUsuario = (user?.email || '').toLowerCase();
  const yoCumplo = cumpleHoy.find((c) => c.email === emailUsuario) || null;
  const otrosHoy = cumpleHoy.filter((c) => c.email !== emailUsuario);
  const primerNombre = yoCumplo ? (yoCumplo.nombre || '').trim().split(/\s+/)[0] : '';

  return (
    <>
      <style>{estilosCumple}</style>
      <div className="cumple-widget-container" style={{ maxWidth: '1200px', margin: isMobile ? '15px 0' : '30px auto' }}>
        {yoCumplo ? (
          // ===== Felicitación personal al cumpleañero =====
          <div className="card cumple-card" style={{
            ...ESTILO_TILE,
            padding: isMobile ? '16px' : '20px 24px',
            backgroundColor: 'var(--color-primario-suave)',
            border: '1px solid var(--color-primario-borde)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexDirection: isMobile ? 'column' : 'row',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            <span className="cumple-icono-festivo" style={{ color: 'var(--color-primario-fuerte)', display: 'inline-flex', flexShrink: 0 }}>
              <CakeIcon className={isMobile ? 'w-10 h-10' : 'w-12 h-12'} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 'bold', color: 'var(--color-texto)', display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                ¡Feliz cumpleaños, {primerNombre}!
                <span style={{ color: 'var(--color-advertencia-texto)', display: 'inline-flex' }}><SparklesIcon className="w-5 h-5" /></span>
              </div>
              <div style={{ fontSize: '13px', color: 'var(--color-texto-secundario)', marginTop: '4px' }}>
                Todo el equipo de Paralellum te desea un excelente día. Gracias por tu trabajo.
              </div>
              {otrosHoy.length > 0 && (
                <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginTop: '6px' }}>
                  También {otrosHoy.length === 1 ? 'cumple años hoy' : 'cumplen años hoy'}: <strong>{otrosHoy.map((c) => c.nombre).join(', ')}</strong>
                </div>
              )}
            </div>
          </div>
        ) : (
          // ===== Recordatorio para el resto del equipo =====
          <div className="card cumple-card" style={{ ...ESTILO_TILE, padding: isMobile ? '12px' : '14px 20px' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ display: 'inline-flex', color: 'var(--color-primario-fuerte)' }}><CakeIcon className="w-4 h-4" /></span> Cumpleaños del equipo
            </div>
            {cumpleHoy.length > 0 && (
              <div style={{ fontSize: isMobile ? '15px' : '17px', fontWeight: 'bold', color: 'var(--color-texto)', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span className="cumple-icono-festivo" style={{ color: 'var(--color-primario-fuerte)', display: 'inline-flex' }}><CakeIcon className="w-6 h-6" /></span>
                Hoy {cumpleHoy.length === 1 ? 'cumple años' : 'cumplen años'} {cumpleHoy.map((c) => c.nombre).join(', ')} — ¡no te olvides de felicitar{cumpleHoy.length === 1 ? 'le' : 'les'}!
              </div>
            )}
            {proximos.length > 0 && (
              <div style={{
                fontSize: '12px',
                color: 'var(--color-texto-secundario)',
                marginTop: cumpleHoy.length > 0 ? '10px' : 0,
                paddingTop: cumpleHoy.length > 0 ? '8px' : 0,
                borderTop: cumpleHoy.length > 0 ? '1px solid var(--color-borde-suave)' : 'none',
                display: 'flex',
                alignItems: 'baseline',
                gap: '6px',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontWeight: 'bold' }}>Próximos:</span>
                {proximos.map((p) => (
                  <span key={p.email || p.nombre} style={{
                    backgroundColor: 'var(--color-fondo)',
                    border: '1px solid var(--color-borde-suave)',
                    borderRadius: 'var(--radio-sm)',
                    padding: '2px 8px',
                    whiteSpace: 'nowrap'
                  }}>
                    {p.nombre} · {p.fechaTexto} ({p.enDias === 1 ? 'mañana' : `en ${p.enDias} días`})
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default CumpleanosWidget;
